import React, { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { CheckCircle, Mail, RefreshCw, ArrowLeft } from 'lucide-react'
import { authService } from '../services/api'

const VerifyCode = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const inputRefs = useRef([])
  
  // Get email from location state (passed from registration)
  const email = location.state?.email || ''

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [])

  const handleInputChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return
    
    const newCode = [...code]
    newCode[index] = value.slice(-1) // Take only the last digit
    setCode(newCode)
    setError('')

    // Auto-focus next input
    if (value && index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus()
    }

    // Auto-verify when all 6 digits are entered
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      handleVerifyCode(newCode.join(''))
    }
  }

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6)
        if (digits.length === 6) {
          const newCode = digits.split('')
          setCode(newCode)
          handleVerifyCode(digits)
        }
      })
    }
  }

  const handleVerifyCode = async (verificationCode = code.join('')) => {
    if (verificationCode.length !== 6) {
      setError('Please enter all 6 digits')
      return
    }

    try {
      setIsVerifying(true)
      setError('')
      
      const response = await authService.verifyEmailCode({
        email: email,
        code: verificationCode
      })
      
      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! You can now sign in.',
              email: email
            }
          })
        }, 2000)
      } else {
        throw new Error(response.message || 'Invalid verification code')
      }
    } catch (error) {
      console.error('Code verification error:', error)
      setError(error.response?.data?.message || 'Invalid verification code. Please try again.')
      // Clear the code on error
      setCode(['', '', '', '', '', ''])
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus()
      }
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResendCode = async () => {
    if (!email) {
      setError('Email address not found. Please register again.')
      return
    }

    try {
      setIsResending(true)
      setError('')
      
      const response = await authService.resendVerificationCode(email)
      
      if (response.success) {
        // Show success message briefly
        setError('')
        // You could show a success toast here instead
        alert('New verification code sent! Please check your email.')
      } else {
        throw new Error(response.message || 'Failed to send verification code')
      }
    } catch (error) {
      console.error('Resend code error:', error)
      setError(error.response?.data?.message || 'Failed to send verification code')
    } finally {
      setIsResending(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
              <p className="text-gray-600 mb-4">Your email has been verified successfully.</p>
              <p className="text-sm text-gray-500">Redirecting you to login page...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="text-center">
            <Mail className="mx-auto h-12 w-12 text-primary-600" />
            <h1 className="mt-6 text-3xl font-extrabold text-gray-900">Verify Your Email</h1>
            <p className="mt-2 text-sm text-gray-600">
              Enter the 6-digit code sent to
            </p>
            <p className="text-sm font-medium text-primary-600">
              {email || 'your email address'}
            </p>
          </div>
        </div>

        {/* Verification Form */}
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <div className="space-y-6">
            {/* Code Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <div className="flex space-x-2 justify-center">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-bold border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    disabled={isVerifying}
                  />
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Enter the 6-digit code from your email
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Verify Button */}
            <button
              onClick={() => handleVerifyCode()}
              disabled={isVerifying || code.some(digit => digit === '')}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isVerifying ? (
                <div className="flex items-center">
                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                  Verifying...
                </div>
              ) : (
                'Verify Email'
              )}
            </button>

            {/* Resend Code */}
            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">Didn't receive the code?</p>
              <button
                onClick={handleResendCode}
                disabled={isResending}
                className="text-primary-600 hover:text-primary-500 text-sm font-medium focus:outline-none"
              >
                {isResending ? 'Sending...' : 'Resend verification code'}
              </button>
            </div>

            {/* Security notice */}
            <div className="text-center">
              <p className="text-sm text-gray-600">
                🔒 For enhanced security, we only use verification codes
              </p>
              <p className="text-sm text-gray-500 mt-1">
                لأسباب أمنية، نستخدم رموز التحقق فقط
              </p>
            </div>

            {/* Back to Login */}
            <div className="text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to login
              </Link>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="text-center">
          <p className="text-xs text-gray-600">
            Need help? Contact us at{' '}
            <a href="mailto:q8.3qarat@gmail.com" className="text-primary-600 hover:text-primary-500">
              q8.3qarat@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default VerifyCode
